import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  fetchPriceBreakdown,
  type PriceBreakdownResponse,
  type PriceBreakdownRow,
  type ResidualClassification,
} from "../api/cars";

// Traffic-light colours keyed on residual classification.
const MARK_COLOR_CLASS: Record<ResidualClassification, string> = {
  well_below: "bg-green-600",
  below: "bg-green-500",
  at_market: "bg-amber-400",
  above: "bg-red-500",
  well_above: "bg-red-600",
};

const RESIDUAL_BG: Record<ResidualClassification, string> = {
  well_below: "bg-green-100",
  below: "bg-green-50",
  at_market: "bg-gray-50",
  above: "bg-red-50",
  well_above: "bg-red-100",
};

function formatPounds(pence: number): string {
  const pounds = Math.round(pence / 100);
  const sign = pounds < 0 ? "−" : "";
  return `${sign}£${Math.abs(pounds).toLocaleString("en-GB")}`;
}

function formatDelta(pence: number): string {
  const pounds = Math.round(pence / 100);
  if (pounds === 0) return "£0";
  const sign = pounds < 0 ? "−" : "+";
  return `${sign}£${Math.abs(pounds).toLocaleString("en-GB")}`;
}

function formatFittedAt(iso: string): string {
  return new Date(iso).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "Europe/London",
  });
}

// ---------------------------------------------------------------------------
// Scale indicator — shows asking price vs expected on a 0…2× scale.
// ---------------------------------------------------------------------------

function PriceScaleIndicator({
  expectedPence,
  uncertaintyPence,
  listedPence,
  classification,
  noPeers,
}: {
  expectedPence: number;
  uncertaintyPence: number | null;
  listedPence: number;
  classification: ResidualClassification | null;
  noPeers?: boolean;
}) {
  const maxPence = 2 * expectedPence;
  if (maxPence <= 0) return null;

  const clamp = (v: number) => Math.min(Math.max(v, 0), 100);
  const markPct = clamp((listedPence / maxPence) * 100);

  let bandLeftPct = 50;
  let bandRightPct = 50;
  if (uncertaintyPence != null && uncertaintyPence > 0) {
    bandLeftPct = clamp(((expectedPence - uncertaintyPence) / maxPence) * 100);
    bandRightPct = clamp(((expectedPence + uncertaintyPence) / maxPence) * 100);
  }

  // gck303/evtracker#60: a "no comparable peers" car's classification isn't a
  // meaningful value signal (the fit essentially reproduces its own price),
  // so the mark is neutral grey regardless of what the fallback classifier
  // would otherwise colour it.
  const markColorClass = noPeers
    ? "bg-gray-400"
    : classification
      ? MARK_COLOR_CLASS[classification]
      : "bg-gray-400";

  return (
    <div className="mb-4" data-testid="price-scale-indicator">
      {/* Track */}
      <div className="relative h-5 bg-gray-100 rounded overflow-hidden">
        {/* Uncertainty band */}
        {bandRightPct > bandLeftPct && (
          <div
            className="absolute inset-y-0 bg-blue-100"
            style={{ left: `${bandLeftPct}%`, width: `${bandRightPct - bandLeftPct}%` }}
            aria-hidden="true"
          />
        )}
        {/* Expected midpoint tick */}
        <div
          className="absolute inset-y-0 w-px bg-blue-400"
          style={{ left: "50%" }}
          aria-hidden="true"
        />
        {/* Asking price line */}
        <div
          className={`absolute inset-y-0 w-0.5 ${markColorClass}`}
          style={{ left: `${markPct}%` }}
          aria-hidden="true"
        />
      </div>
      {/* Labels */}
      <div className="flex justify-between text-xs text-gray-400 mt-0.5 select-none">
        <span>£0</span>
        <span className="text-blue-500">{formatPounds(expectedPence)} expected</span>
        <span>{formatPounds(maxPence)}</span>
      </div>
      <div className="text-xs text-center mt-0.5">
        <span className={`font-medium ${classification ? "text-gray-700" : "text-gray-500"}`}>
          Asking {formatPounds(listedPence)}
        </span>
        {uncertaintyPence != null && (
          <span className="text-gray-400 ml-1">
            (expected ±{formatPounds(uncertaintyPence)})
          </span>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Diverging bar layout
// ---------------------------------------------------------------------------

interface DivergingBar {
  leftPct: number;
  widthPct: number;
}

interface ScaledRow {
  row: PriceBreakdownRow;
  bar: DivergingBar | null;
}

// A build-up row carries a £ delta that contributes to the expected price and
// gets a diverging bar: spec adjustments and the luxury-tax saving.
function isBuildupDelta(row: PriceBreakdownRow): boolean {
  if (row.delta_pence == null) return false;
  if (row.kind === "adjustment") return !row.is_null_effect;
  return row.kind === "luxury_tax";
}

function scaleRows(rows: PriceBreakdownRow[]): ScaledRow[] {
  const deltas = rows.filter(isBuildupDelta).map((r) => Math.abs(r.delta_pence!));

  const maxDelta = Math.max(...deltas, 1);

  return rows.map((row) => {
    if (isBuildupDelta(row)) {
      const delta = row.delta_pence!;
      const halfPct = (Math.abs(delta) / maxDelta) * 50;
      const bar: DivergingBar =
        delta >= 0
          ? { leftPct: 50, widthPct: halfPct }
          : { leftPct: 50 - halfPct, widthPct: halfPct };
      return { row, bar };
    }
    return { row, bar: null };
  });
}

function adjustmentFillClass(row: PriceBreakdownRow): string {
  if (row.delta_pence == null) return "bg-gray-200";
  return row.delta_pence < 0 ? "bg-red-300" : "bg-green-300";
}

function rowAmountText(row: PriceBreakdownRow): string {
  if (row.kind === "baseline" && row.value_pence != null) return formatPounds(row.value_pence);
  if (row.kind === "residual" && row.value_pence != null) return formatDelta(row.value_pence);
  if (row.kind === "buyer_adjusted" && row.value_pence != null) return formatDelta(row.value_pence);
  if (row.kind === "luxury_tax" && row.delta_pence != null) return formatDelta(row.delta_pence);
  if (row.kind === "adjustment" && row.delta_pence != null) {
    if (row.is_null_effect) return "£0";
    return formatDelta(row.delta_pence);
  }
  return "";
}

function nullEffectTooltip(dimension: string): string {
  return (
    `On the current data, ${dimension} has no measurable effect on price ` +
    "individually — each is too close to zero to price on its own, but the " +
    "model still fits (and this figure sums) their combined contribution."
  );
}

function baselineTooltip(modelCode: string): string {
  // §8.7 (M12.B). Mean-spec framing — populated only on centred fits.
  return `What the average ${modelCode} in current stock with reference paint, interior, wheels, and no packs would list at.`;
}

const RESIDUAL_TOOLTIP =
  "Listed price minus what the model expects, given this car's model year, mileage, and spec. " +
  "This is the value verdict — how this car compares to its peers.";

const LUXURY_TAX_TOOLTIP =
  "VED Expensive Car Supplement saving. Cars first registered on/after 1 April 2025 " +
  "carry an annual surcharge over their first five years, which a used buyer inherits. " +
  "A car registered before the cutoff is exempt, so it is worth that remaining tax more " +
  "to a buyer than an equivalent taxed car — a buyer-specific fact, shown below the value " +
  "verdict above, which it does not affect. A car that owes the tax saves nothing (£0).";

const BUYER_ADJUSTED_TOOLTIP =
  "Listed price minus (what the model expects, plus the luxury-tax saving above). A buyer's " +
  "own view of the price — it does not change the value verdict, band, or ranking above.";

const NO_PEERS_TOOLTIP =
  "This car's spec is unique enough in the current fit (high leverage) that the model " +
  "essentially reproduces its own price — the band above isn't a meaningful value signal " +
  "for it. gck303/evtracker#60.";

function BreakdownRowView({ scaled }: { scaled: ScaledRow }) {
  const { row, bar } = scaled;

  if (row.kind === "null_effects_summary") {
    const dimension = row.sublabel ?? row.label;
    return (
      <div
        data-testid="breakdown-row"
        data-kind="null_effects_summary"
        title={nullEffectTooltip(dimension)}
        className="grid grid-cols-1 min-[600px]:grid-cols-[minmax(0,1fr)_minmax(0,2fr)_auto] items-center gap-2 min-[600px]:gap-4 py-1 text-gray-400"
      >
        <div className="min-w-0">
          <div className="text-sm">{row.label}</div>
          {row.sublabel && (
            <div className="text-xs text-gray-400 truncate">{row.sublabel}</div>
          )}
        </div>
        <div className="h-2 relative" aria-hidden="true" />
        <div className="text-sm tabular-nums text-right text-gray-400">
          {row.delta_pence ? formatDelta(row.delta_pence) : "no measurable effect"}
        </div>
      </div>
    );
  }

  const isNullEffectAdjustment = row.kind === "adjustment" && row.is_null_effect;
  const labelTextClass = isNullEffectAdjustment ? "text-gray-400" : "text-gray-800";

  // gck303/evtracker#60: a "no comparable peers" residual isn't a meaningful
  // value signal (see NO_PEERS_TOOLTIP) — render it neutral rather than in
  // whatever colour the fallback classification happens to land on.
  const isNoPeersResidual = row.kind === "residual" && Boolean(row.no_peers);
  const bgClass = isNoPeersResidual
    ? "bg-gray-50"
    : row.kind === "residual" && row.classification
      ? RESIDUAL_BG[row.classification]
      : "";

  const isCentredBaseline =
    row.kind === "baseline" && (row.sublabel ?? "").startsWith("Mean-spec ");

  const isBuyerRow = row.kind === "luxury_tax" || row.kind === "buyer_adjusted";

  const tooltip =
    isNullEffectAdjustment
      ? nullEffectTooltip(row.label)
      : isNoPeersResidual
        ? NO_PEERS_TOOLTIP
        : row.kind === "residual"
          ? RESIDUAL_TOOLTIP
          : row.kind === "luxury_tax"
            ? LUXURY_TAX_TOOLTIP
            : row.kind === "buyer_adjusted"
              ? BUYER_ADJUSTED_TOOLTIP
              : isCentredBaseline
                ? baselineTooltip((row.sublabel ?? "").slice("Mean-spec ".length).trim())
                : undefined;

  // Residual row label is always "Difference" — the scale indicator above
  // shows the classification visually — except a no-peers car, which gets an
  // explicit label since there's no meaningful badge to look at instead.
  const displayLabel =
    row.kind === "residual" ? (isNoPeersResidual ? "No comparable peers" : "Difference") : row.label;

  return (
    <div
      data-testid="breakdown-row"
      data-kind={row.kind}
      data-classification={row.classification ?? undefined}
      data-no-peers={isNoPeersResidual ? "true" : undefined}
      title={tooltip}
      className={`grid grid-cols-1 min-[600px]:grid-cols-[minmax(0,1fr)_minmax(0,2fr)_auto] items-center gap-2 min-[600px]:gap-4 py-1 ${bgClass} ${labelTextClass}`}
    >
      <div className="min-w-0">
        <div className="text-sm font-medium">
          {isBuyerRow && (
            <span
              data-testid="buyer-row-badge"
              className="mr-1.5 align-middle text-[10px] font-semibold uppercase tracking-wide text-blue-500"
            >
              Buyer view
            </span>
          )}
          {displayLabel}
        </div>
        {row.sublabel && (
          <div className="text-xs text-gray-500 truncate" title={row.sublabel}>
            {row.sublabel}
          </div>
        )}
      </div>

      {/* Bar column: diverging from centerline for adjustments, blank otherwise */}
      <div className="h-2 relative bg-gray-100 rounded-sm overflow-hidden" aria-hidden="true">
        {bar ? (
          <>
            {/* Centerline at 50% */}
            <div className="absolute inset-y-0 w-px bg-gray-400" style={{ left: "50%" }} />
            {/* Diverging bar */}
            <div
              className={`absolute inset-y-0 ${adjustmentFillClass(row)} rounded-sm`}
              style={{ left: `${bar.leftPct}%`, width: `${bar.widthPct}%` }}
              data-testid="breakdown-bar"
            />
          </>
        ) : null}
      </div>

      <div className="text-sm font-medium tabular-nums text-right">{rowAmountText(row)}</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main view — scale indicator at top, adjustment table below.
// ---------------------------------------------------------------------------

export function PriceBreakdownView({ data }: { data: PriceBreakdownResponse }) {
  const isEmpty = data.regression_fitted_at === null || data.rows.length === 0;

  const expectedRow = data.rows.find((r) => r.kind === "expected");
  const listedRow = data.rows.find((r) => r.kind === "listed");
  const residualRow = data.rows.find((r) => r.kind === "residual");

  // The expected and listed rows move to the scale indicator; exclude them from the table.
  const tableRows = data.rows.filter((r) => r.kind !== "expected" && r.kind !== "listed");

  // #59: luxury_tax and buyer_adjusted are buyer-side rows, trailing the market
  // verdict (baseline…residual) and never feeding back into it — split them into
  // their own labelled block rather than let them read as more build-up lines
  // towards `expected`.
  const marketRows = tableRows.filter(
    (r) => r.kind !== "luxury_tax" && r.kind !== "buyer_adjusted",
  );
  const buyerRows = tableRows.filter(
    (r) => r.kind === "luxury_tax" || r.kind === "buyer_adjusted",
  );

  return (
    <section className="mt-6" data-testid="price-breakdown">
      <header className="flex items-baseline justify-between gap-4 mb-3">
        <div>
          <h2 className="text-lg font-semibold">Price breakdown</h2>
          <p className="text-xs text-gray-500">Show the working</p>
        </div>
        <Link
          to="/about/price-breakdown"
          className="text-sm text-blue-600 hover:underline shrink-0"
        >
          How is this calculated?
        </Link>
      </header>

      {isEmpty ? (
        <p className="text-sm text-gray-500 bg-gray-50 border rounded p-3">
          Not enough cars of this model in current inventory to fit a reliable model. We&apos;ll
          start showing this once we have more data.
        </p>
      ) : (
        <>
          {expectedRow?.value_pence != null && listedRow?.value_pence != null && (
            <PriceScaleIndicator
              expectedPence={expectedRow.value_pence}
              uncertaintyPence={expectedRow.uncertainty_pence ?? null}
              listedPence={listedRow.value_pence}
              classification={residualRow?.classification ?? null}
              noPeers={Boolean(residualRow?.no_peers)}
            />
          )}
          <div className="rounded border bg-white px-3 py-2 divide-y divide-gray-100">
            {scaleRows(marketRows).map((scaled, i) => (
              <BreakdownRowView key={i} scaled={scaled} />
            ))}
          </div>
          {buyerRows.length > 0 && (
            <div data-testid="buyer-adjustment-block" className="mt-3">
              <p className="text-xs font-medium text-gray-500 mb-1">
                Buyer adjustment — doesn&apos;t affect the value verdict above
              </p>
              <div className="rounded border bg-blue-50/40 px-3 py-2 divide-y divide-blue-100">
                {scaleRows(buyerRows).map((scaled, i) => (
                  <BreakdownRowView key={i} scaled={scaled} />
                ))}
              </div>
            </div>
          )}
          {data.regression_fitted_at && data.regression_n_observations != null && (
            <p
              className="mt-2 text-xs text-gray-500"
              data-testid="breakdown-disclosure"
            >
              Based on a regression of {data.regression_n_observations}{" "}
              {data.model_code ?? ""} cars listed in the last 12 months (asking
              price; sold prices are unavailable), fitted{" "}
              {formatFittedAt(data.regression_fitted_at)}.
            </p>
          )}
        </>
      )}
    </section>
  );
}

export default function PriceBreakdown({ carId }: { carId: string }) {
  const [data, setData] = useState<PriceBreakdownResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchPriceBreakdown(carId)
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(String(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [carId]);

  if (loading) {
    return (
      <p className="mt-6 text-sm text-gray-400" data-testid="price-breakdown-loading">
        Loading price breakdown…
      </p>
    );
  }
  if (error) {
    return (
      <p
        className="mt-6 text-sm text-red-600 bg-red-50 border border-red-200 rounded p-3"
        data-testid="price-breakdown-error"
      >
        Could not load price breakdown: {error}
      </p>
    );
  }
  if (!data) return null;
  return <PriceBreakdownView data={data} />;
}
